import React from "react";
import { Banner, Button, FormField, Input, StatusPill } from "@/ds";
import { cockpit } from "@/api";
import type { TenantInvitation } from "@/api";
import { useLang, useT } from "../i18n";
import { invitationRows, invitationUrl } from "./invitation.logic";
import type { PushToast } from "@/notifications/toast-queue";
import styles from "./invitation-panel.module.css";

/**
 * The owner's side of handing a business over (#780), in three blocks (#1164): the link just
 * made, the links so far, the form for the next one.
 *
 * The link is the whole credential, so the panel is built around the one fact that follows from
 * that: it exists in readable form exactly once, in the response to creating it. There is no
 * "show link again", because there is nothing to show — the server keeps only a hash. Saying that
 * plainly, at the moment the link appears, is the difference between an owner copying it now and
 * an owner coming back tomorrow to look for it. Which is also why the link sits in a field that
 * selects itself and has one button next to it: copying must be the easiest thing on the page.
 *
 * Reissuing is offered rather than hidden, and it says what it costs: the previous link stops
 * working. Two live links for one business means one is circulating that nobody remembers sending.
 *
 * Since #1305 the server also emails the link when an address was given, and the reissued mail says
 * that any earlier link has stopped working. The address stays optional, so the panel reports three
 * things rather than two: sent, not sent, or nobody to send to.
 */

/** How long "Kopiert" stays on the button before it offers to copy again. */
const COPIED_FEEDBACK_MS = 2000;

interface FreshLink {
  url: string;
  expiresAt: string;
  invitedEmail: string | null;
  /**
   * Whether the server emailed the link (#1305). `null` means there was nobody to email it to,
   * which is a normal way to prepare a business — so the panel says nothing about delivery rather
   * than reporting a failure that did not happen.
   */
  emailSent: boolean | null;
}

function pillTone(state: TenantInvitation["state"]): "success" | "neutral" | "warning" {
  if (state === "outstanding") return "success";
  if (state === "accepted") return "neutral";
  return "warning";
}

/** Newest first: the link an owner is looking for is almost always the one they just made. */
export function sortInvitations(rows: TenantInvitation[]): TenantInvitation[] {
  return [...rows].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
}

export function InvitationPanel({
  tenantId,
  pushToast,
}: {
  tenantId: string;
  pushToast: PushToast;
}) {
  const t = useT();
  const { lang } = useLang();
  const [rows, setRows] = React.useState<TenantInvitation[] | null>(null);
  const [email, setEmail] = React.useState("");
  const [name, setName] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  /** Held in memory only, and only until the panel is left. The server cannot reproduce it. */
  const [fresh, setFresh] = React.useState<FreshLink | null>(null);
  const [copied, setCopied] = React.useState(false);
  const copyRowRef = React.useRef<HTMLDivElement>(null);

  /**
   * Carries the recipient of the outstanding link into the form (#1305).
   *
   * Issuing revokes whatever link is outstanding, so a reissue with an empty address kills the link
   * somebody is holding and then tells nobody — the exact situation the "your previous link no
   * longer works" line in the email exists to prevent. The address belongs to the handover rather
   * than to one click, so an empty form starts from it.
   *
   * Only ever into an empty field, so nothing the owner typed is overwritten, and only from a link
   * that is still outstanding — after a revoke there is nobody to carry forward and the form stays
   * blank. Clearing it remains a deliberate act with its old meaning: prepare the business, tell
   * nobody yet.
   */
  const seedFromOutstanding = React.useCallback((list: TenantInvitation[]) => {
    const outstanding = list.find((row) => row.state === "outstanding");
    if (!outstanding?.invitedEmail) return;
    setEmail((current) => current || outstanding.invitedEmail || "");
    setName((current) => current || outstanding.invitedName || "");
  }, []);

  const formatDate = React.useCallback(
    (iso: string) => new Date(iso).toLocaleDateString(lang === "de" ? "de-DE" : "en-GB"),
    [lang],
  );

  const load = React.useCallback(() => {
    cockpit.listInvitations(tenantId).then((list) => {
      const next = sortInvitations(invitationRows<TenantInvitation>(list));
      setRows(next);
      seedFromOutstanding(next);
    }, () => setRows([]));
  }, [tenantId, seedFromOutstanding]);

  React.useEffect(() => {
    setFresh(null);
    setCopied(false);
    load();
  }, [load]);

  // The moment a link exists, the next thing to do is copy it, so that is where focus goes.
  React.useEffect(() => {
    if (!fresh) return;
    copyRowRef.current?.querySelector("button")?.focus();
  }, [fresh]);

  // "Kopiert" reverts on its own; a stale confirmation on a button is a small lie.
  React.useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  const outstanding = rows?.find((row) => row.state === "outstanding") ?? null;
  // Somebody already accepted, which means the business has an account, and `issue` refuses a
  // business that does. Offering the button anyway would be an affordance whose only outcome is
  // an error toast, so the panel says what happened instead.
  const handedOver = rows?.some((row) => row.state === "accepted") ?? false;

  const issue = () => {
    setBusy(true);
    cockpit
      .issueInvitation(tenantId, { email: email.trim() || undefined, name: name.trim() || undefined })
      .then((issued) => {
        setCopied(false);
        setFresh({
          url: invitationUrl(issued.token, window.location.origin, import.meta.env.BASE_URL || "/"),
          expiresAt: issued.expiresAt,
          invitedEmail: issued.invitedEmail,
          emailSent: issued.emailSent,
        });
        setEmail("");
        setName("");
        load();
      })
      .catch((e: any) => pushToast({ tone: "danger", title: t("common.error"), msg: e?.message }))
      .finally(() => setBusy(false));
  };

  const revoke = (invitationId: string) => {
    setBusy(true);
    cockpit
      .revokeInvitation(tenantId, invitationId)
      .then(() => {
        setFresh(null);
        load();
      })
      .catch((e: any) => pushToast({ tone: "danger", title: t("common.error"), msg: e?.message }))
      .finally(() => setBusy(false));
  };

  const selectWholeLink = (e: React.SyntheticEvent<HTMLInputElement>) => {
    e.currentTarget.select?.();
  };

  const copy = async () => {
    if (!fresh) return;
    try {
      if (!navigator.clipboard) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(fresh.url);
      setCopied(true);
      pushToast({ tone: "success", title: t("invite.owner.copied") });
    } catch {
      // Selecting the text is the honest fallback: the person can still copy with the keyboard,
      // and the toast tells them so instead of pretending the copy happened.
      copyRowRef.current?.querySelector("input")?.select();
      pushToast({ tone: "warning", title: t("invite.owner.copyFailed") });
    }
  };

  return (
    <div className={styles.panel} data-testid="invitation-panel">
      <div className={styles.intro}>
        <div className="neo-eyebrow">{t("invite.owner.title")}</div>
        <p className="subtle" style={{ margin: 0, fontSize: "var(--text-sm)" }}>
          {t("invite.owner.hint")}
        </p>
      </div>

      {fresh && (
        <section
          className={styles.current}
          aria-labelledby="invitation-current-title"
          data-testid="invitation-current"
        >
          <h3 id="invitation-current-title" className={styles.sectionTitle}>
            {t("invite.owner.currentLink")}
          </h3>
          <div className={styles.copyRow} ref={copyRowRef}>
            <Input
              readOnly
              value={fresh.url}
              aria-label={t("invite.owner.currentLinkLabel")}
              onFocus={selectWholeLink}
              onClick={selectWholeLink}
              data-testid="invitation-link-field"
            />
            <div className={styles.copyAction}>
              <Button variant="primary" size="sm" onClick={copy} data-testid="invitation-copy">
                {copied ? t("invite.owner.copiedShort") : t("invite.owner.copy")}
              </Button>
            </div>
          </div>
          {/* Three answers, not two. "Sent" is a footnote; "not sent" is an instruction the owner
              must not scroll past, because the link above is then the only copy anybody has; and
              no address at all is neither, so it says nothing rather than inventing a problem. */}
          {fresh.emailSent === true && (
            <p className={styles.meta} data-testid="invitation-delivery">
              {t("invite.owner.emailed", { email: fresh.invitedEmail ?? "" })}
            </p>
          )}
          {fresh.emailSent === false && (
            <Banner
              tone="warning"
              title={t("invite.owner.notEmailed")}
              data-testid="invitation-delivery"
            />
          )}
          <p className={styles.meta}>
            {t("invite.owner.expires", { date: formatDate(fresh.expiresAt) })}
            {" · "}
            {t("invite.owner.once")}
          </p>
        </section>
      )}

      <section aria-labelledby="invitation-history-title" data-testid="invitation-history">
        <h3 id="invitation-history-title" className={styles.sectionTitle}>
          {t("invite.owner.history")}
        </h3>
        {rows && rows.length === 0 && (
          <p className={`subtle ${styles.empty}`}>{t("invite.owner.none")}</p>
        )}
        {rows && rows.length > 0 && (
          <div className={styles.tableWrap}>
            <table className={styles.table} data-testid="invitation-table">
              <thead>
                <tr>
                  <th scope="col">{t("invite.owner.col.status")}</th>
                  <th scope="col">{t("invite.owner.col.invited")}</th>
                  <th scope="col">{t("invite.owner.col.created")}</th>
                  <th scope="col">{t("invite.owner.col.expires")}</th>
                  <th scope="col">{t("invite.owner.col.action")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} data-testid="invitation-row" data-state={row.state}>
                    <td data-label={t("invite.owner.col.status")}>
                      <StatusPill tone={pillTone(row.state)} label={t(`invite.state.${row.state}`)} />
                    </td>
                    <td data-label={t("invite.owner.col.invited")}>
                      {!row.invitedName && !row.invitedEmail ? (
                        <span className="subtle">—</span>
                      ) : (
                        <>
                          {row.invitedName && <span className={styles.invitedName}>{row.invitedName}</span>}
                          {row.invitedEmail && <span className={styles.invitedEmail}>{row.invitedEmail}</span>}
                        </>
                      )}
                    </td>
                    <td data-label={t("invite.owner.col.created")} className="neo-num">
                      {formatDate(row.createdAt)}
                    </td>
                    <td data-label={t("invite.owner.col.expires")} className="neo-num">
                      {formatDate(row.expiresAt)}
                    </td>
                    <td data-label={t("invite.owner.col.action")} className={styles.actionCell}>
                      {row.state === "outstanding" && (
                        <Button
                          variant="secondary"
                          size="sm"
                          disabled={busy}
                          onClick={() => revoke(row.id)}
                          data-testid="invitation-revoke"
                        >
                          {t("invite.owner.revoke")}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {handedOver ? (
        <p style={{ margin: 0, fontSize: "var(--text-sm)" }} data-testid="invitation-handed-over">
          {t("invite.owner.handedOver")}
        </p>
      ) : (
        <section
          className={styles.form}
          aria-labelledby="invitation-create-title"
          data-testid="invitation-create"
        >
          <h3 id="invitation-create-title" className={styles.sectionTitle}>
            {t("invite.owner.createTitle")}
          </h3>
          <div className={styles.fields}>
            <FormField label={t("invite.owner.emailLabel")}>
              <Input
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                placeholder="inhaber@betrieb.de"
              />
            </FormField>
            <FormField label={t("invite.owner.nameLabel")}>
              <Input
                value={name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
              />
            </FormField>
          </div>
          {outstanding && (
            <Banner
              tone="warning"
              title={t("invite.owner.reissueWarning")}
              data-testid="invitation-reissue-warning"
            />
          )}
          <div className={styles.formActions}>
            <Button
              variant="primary"
              size="sm"
              disabled={busy}
              onClick={issue}
              data-testid="invitation-issue"
            >
              {outstanding ? t("invite.owner.reissue") : t("invite.owner.issue")}
            </Button>
          </div>
        </section>
      )}
    </div>
  );
}
